import "./AcedMenu.scss";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface AcedMenuOption {
  text: string;
  action?: () => void;
  color?: string;
}

export interface AcedMenuProps {
  isMenuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  anchorRef: React.RefObject<HTMLElement>;
  options: AcedMenuOption[];
  onClose?: () => void;
}

const AcedMenu: React.FC<AcedMenuProps> = ({
  isMenuOpen,
  setMenuOpen,
  anchorRef,
  options,
  onClose,
}) => {
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const closeMenu = () => {
    setMenuOpen(false);
    onClose && onClose();
  };
  // Close on outside click or scroll
  useEffect(() => {
    const handleClose = (e: MouseEvent | Event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleScrollOrResize = () => {
      closeMenu();
    };

    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [setMenuOpen, anchorRef]);

  // Recalculate position when opening the menu
  useLayoutEffect(() => {
    if (isMenuOpen && anchorRef.current && menuRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const menu = menuRef.current;

      const menuHeight = menu.offsetHeight;
      const menuWidth = menu.offsetWidth;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = rect.bottom;
      let left = rect.left;

      // Flip vertically if not enough space
      if (rect.bottom + menuHeight > viewportHeight) {
        top = rect.top - menuHeight;
      }

      // Flip horizontally if not enough space
      if (rect.left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 10;
      }

      setPosition({ top, left });
    }
  }, [isMenuOpen, anchorRef]);

  if (!isMenuOpen) return null;

  return (
    <div
      className="aced-menu-container"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <ul ref={menuRef}>
        {options.map((opt, index) => (
          <li
            className={`${opt.action ? "li-active" : "li-inactive"}`}
            onClick={
              opt.action
                ? () => {
                    opt.action?.();
                    closeMenu();
                  }
                : () => {}
            }
            key={index}
            style={opt.color ? { color: opt.color } : {}}
          >
            {opt.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AcedMenu;
